(ns assemble.templates-page.validation
  (:require [assemble.util :as util]
            [camel-snake-kebab.core :refer [->kebab-case]]
            [camel-snake-kebab.extras :refer [transform-keys]]
            [clojure.string :as str]
            [common.db.config :as conf]
            [common.url-template :as url-tpl]
            [lemonpi.domain.interface.asset :as domain.asset]
            [malli.core :as m]
            [malli.error :as me]))

(def config
  (transform-keys ->kebab-case (conf/get-config)))

(defn remove-af-not-supported-file-types [types]
  (remove #{"avif"} types))

(defn asset-file-regex [template-type]
  (let [all-types (apply concat ["html" "css" "js" "json" "aep" "psd" "ai"] (vals domain.asset/supported-file-types))
        template-types (if (= template-type :after-effects)
                         (remove-af-not-supported-file-types all-types)
                         all-types)
        types (str/join "|" template-types)]
    (re-pattern (str "(?i)^.+(\\.(" types "))$"))))

(def paths-schema [:sequential [:re (asset-file-regex :html)]])

(def ae-paths-schema [:sequential [:re (asset-file-regex :after-effects)]])

(def font-advertiser-id-regex #".*/a/(\d+)/f.*")

(def assemble-font-advertiser-id-regex #".*/a/font/(\d+)/.*")

(def ad-size-schema
  [:map
   [:width [:int {:min 1 :max js/Number.MAX_SAFE_INTEGER
                  :error/message "Should be greater than 1"}]]
   [:height [:int {:min 1 :max js/Number.MAX_SAFE_INTEGER
                   :error/message "Should be greater than 1"}]]])

(def ^{:doc "note that this is a recursive schema"}
  template-json-schema
  [:schema
   {:registry {::placeholders
               [:and
                [:map-of :string
                 [:or
                  [:map {:closed true}
                   ["type" [:enum "text" "image" "audio" "video" "click"]]
                   ["preview_content" {:optional true} :string]
                   ["layers" {:optional true} [:vector [:or
                                                        string?
                                                        map?]]]
                   ["value" {:optional true} :string]]
                  [:map {:closed true}
                   ["type" [:= "collection"]]
                   ["intent" {:optional true} [:enum "product"]]
                   ["items_required" :int]
                   ["placeholders" [:ref ::placeholders]]]]]
                [:fn {:error/message "placeholders empty"} (fn [placeholders] (> (count placeholders) 0))]]}}
   [:map {:closed true}
    ["placeholders" ::placeholders]
    ["composition" {:optional true} :string]
    ["compositions" {:optional true} :map]
    ["creation_tool" {:optional true} :string]
    ["description" {:optional true} :string]
    ["version" {:optional true} :string]
    ["pre_made_template_id" {:optional true} :int]
    ["isDynamicTimelineEnabled" {:optional true} :boolean]]])

(def aftereffects-paths-schema
  [:and
   [:vector :string]
   [:fn {:error/message ["Multiple '.aep' files found" "We've found more than 1 .aep file, where only 1 is allowed. Check the template folder and try again."]}
    (fn [paths]
      (->> (map str/lower-case paths)
           (filter #(str/ends-with? % "aep"))
           count
           (= 1)))]])

(defn placeholder-type-message [types]
  (let [multiple (> (count types) 1)]
    (str "Placeholder" (when multiple "s")
         " '" (first types)
         (when multiple (str "' and '" (second types)))
         (if multiple "' have" "' has")
         (when (not multiple) " an")
         " unsupported type" (when multiple "s") ". Check for typos and try again.
  See list with supported placeholder types at ")))

(defn file-type-message [types]
  (let [multiple (> (count types) 1)]
    (str "File type" (when multiple "s")
         " '" (util/file-extension (first types))
         (when multiple (str "' and '" (util/file-extension (second types))))
         (if multiple "' are" "' is")
         " not supported. Check the template folder and try again.
  See list with supported file types at ")))

(defn validate-zip-paths [schema paths]
  (when-not (m/validate schema paths)
    (let [details (->> paths
                       (m/explain schema)
                       :errors
                       (map :value))]
      {:message ["Unsupported file type found"
                 (file-type-message details)
                 (url-tpl/render :docs/article (assoc config :slug "24177040477980-Creative-Media-Assets"))]
       :details details})))

(defn validate-ae-zip-paths [paths]
  (when-not (m/validate aftereffects-paths-schema paths)
    (let [details (->> paths
                       (m/explain aftereffects-paths-schema)
                       (me/humanize))]
      {:message (first details)
       :details details})))

(defn validate-ad-size [ad-size]
  (if ad-size
    (when-not (m/validate ad-size-schema ad-size)
      (let [details (->> (m/explain ad-size-schema ad-size)
                         (me/humanize))]
        {:message (case (first (keys details))
                    :height ["height property not found" "We couldn't find the height property. Check the template and try again."]
                    :width ["width property not found" "We couldn't find the width property. Check the template and try again."]
                    ["width or height property not found" "We couldn't find the width or height property. Check the template and try again."])
         :details details}))
    {:message ["ad-size is missing" "We couldn't find ad-size meta tag in the index.html. Check the template and try again."]}))

(defn extract-advertiser-fonts [regex html-source]
  (let [doc (.parseFromString (js/DOMParser.) html-source "text/html")
        links (.querySelectorAll doc "link[href]")]
    (->> (keep #(.-href %) links)
         (reduce (fn [acc path]
                   (let [advertiser-id (-> (re-find regex path)
                                           (second)
                                           (js/parseInt))]
                     (if-not (js/Number.isNaN advertiser-id)
                       (update acc advertiser-id conj path)
                       acc))) {}))))

(defn validate-fonts [html-source advertiser-id]
  (let [fonts (merge-with concat
                          (extract-advertiser-fonts font-advertiser-id-regex html-source)
                          (extract-advertiser-fonts assemble-font-advertiser-id-regex html-source))
        ids (set (keys fonts))]
    (when (and (seq ids) (not= #{advertiser-id} ids))
      {:message ["Font file not found" "We couldn't find the font files used in the template. Check the advertiser's fonts and try again."]
       :details (->> (dissoc fonts advertiser-id)
                     (vals)
                     (apply concat))})))

(defn validate-template-json [json-source]
  (if (or (= json-source :cannot-read-template-json)
          (nil? json-source))
    {:message ["template.json not found" "We couldn't find template.json. Check the template folder and try again."]}
    (if (str/blank? json-source)
      {:message ["template.json is empty" "template.json appears to be empty. Check the file and try again."]}
      (let [parsed (js->clj (js/JSON.parse json-source))]
        (when-not (m/validate template-json-schema parsed)
          (let [details (->> parsed
                             (m/explain template-json-schema)
                             (me/humanize))]
            {:message (case (first (keys details))
                        "composition" ["No placeholders found in template"
                                       "We couldn't find placeholders in this template. Check the file and try again."]
                        "placeholders" (if (= "placeholders empty" (first (get details "placeholders")))
                                         ["No placeholders found in template"
                                          "We couldn't find placeholders in this template. Check the file and try again."]
                                         ["Unsupported placeholder type found in template"
                                          (placeholder-type-message (-> details (get "placeholders") keys))
                                          (url-tpl/render :docs/article (assoc config :slug "24177126726812-Templates"))]))
             :details details}))))))

(defn validate-template-type [selected-type type]
  (when (and (some? selected-type) (not= selected-type type))
    {:message ["Template revision doesn't match template type" "The template revision you want to upload doesn't match the existing template type. Check the template and try again."]
     :details {}}))

(defn unknown-template-type-message []
  ["index.html or .aep file not found"
   "We couldn't detect the template type because it's missing an index.html or an .aep file. Check the template folder and try again."])
